import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-locations-overview-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './locations-overview-chart.component.html',
  styleUrls: ['./locations-overview-chart.component.css'],
})
export class LocationsOverviewChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummylabels: string[] = ['Loc. 1 Milan', 'Loc. 2 Naples', 'Loc. 3 Turin', 'Loc. 4 Palermo', 'Loc. 5 Bologna', 'Loc. 6 Messina'];
  dummyelectricityData: number[] = [6000, 7000, 6500, 6200, 6800, 7000];
  dummygasData: number[] = [3000, 3200, 3100, 2900, 3300, 3100];


  @Input() labels: string[] = ['Loc. 1 Milan', 'Loc. 2 Naples', 'Loc. 3 Turin', 'Loc. 4 Palermo', 'Loc. 5 Bologna', 'Loc. 6 Messina'];

  electricityLabel: string = "Electricity";
  gasLabel: string = "Gas";

  @Input() electricityData: number[] = [6000, 7000, 6500, 6200, 6800, 7000];
  @Input() gasData: number[] = [3000, 3200, 3100, 2900, 3300, 3100];

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();

    combineLatest([
      this.translocoService.selectTranslate('shared.electricity'),
      this.translocoService.selectTranslate('shared.gas'),
    ]).subscribe(([electricityTranslation, gasTranslation]) => {
      this.electricityLabel = electricityTranslation;
      this.gasLabel = gasTranslation;
      this.updateChart();
    });
  }

  toggleDataset(index: number, hidden: boolean | null = null): void {
    if (this.data && this.data.datasets && this.data.datasets[index]) {
      if (hidden === null) {
        this.data.datasets[index].hidden = !this.data.datasets[index].hidden;
      }
      else {
        this.data.datasets[index].hidden = hidden;
      }
      this.data = { ...this.data };
    }
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.labels = this.dummylabels;
      this.electricityData = this.dummyelectricityData;
      this.gasData = this.dummygasData;
    }

    Chart.register(ChartDataLabels);

    this.data = {
      labels: this.labels,
      datasets: [
        {
          label: this.electricityLabel,
          data: this.electricityData,
          backgroundColor: '#A9BE92',
          borderRadius: 5,
          stack: 'Stack 0',
        },
        {
          label: this.gasLabel,
          data: this.gasData,
          backgroundColor: '#80A7A5',
          borderRadius: 5,
          stack: 'Stack 0',
          datalabels: {
            align: 'center',
            anchor: 'end',
            color: '#000000',
            display: 'auto',
            clamp: true,
            font: {
              size: 10,
              weight: 'normal',
              family: 'Gilroy',
            },
            formatter: (value: any, context: any) => {
              if (!value || value === 0) { return null; }
              const dataIndex = context.dataIndex;
              const datasets = context.chart.data.datasets;

              const sum = datasets
                .filter((dataset: any) => dataset.stack === context.dataset.stack)
                .reduce((acc: any, dataset: any) => acc + (dataset.data[dataIndex] || 0), 0);

              const valueString = `${this.decimalPipe.transform(value, '1.0-0') || value}€`;
              const spaces = ' '.repeat(valueString.length);

              return `${valueString}${spaces}${this.decimalPipe.transform(sum, '1.0-0') || sum}€`;
            },
          },
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            font: {
              size: 10,
            },
          },
        },
        datalabels: {
          display: 'auto',
          clamp: true,
          color: 'black',
          font: {
            size: 10,
            weight: 'normal',
            family: 'Gilroy',
          },
          formatter: (value: any) => {
            if (!value || value === 0) { return null; }
            return `${this.decimalPipe.transform(value, '1.0-0') || value}€`
          },
        },
        tooltip: {
          callbacks: {
            label: this.formatTooltipLabel.bind(this)
          }
        }
      },
      indexAxis: 'y',
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            display: true,
            borderDash: [5, 5],
          },
        },
        y: {
          ticks: {
            font: {
              size: 12,
            },
          },
          grid: {
            display: false,
          },
        },
      },
    };
  }

  formatTooltipLabel(tooltipItem: any) {
    let label = tooltipItem.dataset.label || '';
    let value = tooltipItem.raw || 0;
    return `${label}: ${this.decimalPipe.transform(value, '1.0-0') || value}€`;
  }
}
